import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

// Reactions for DMs, lifted from Closing Credits' course chat so the two feel
// the same: a hover-bar smiley and a dashed "+" both open one emoji grid,
// pills toggle your own reaction, and hovering (or holding, on touch) a pill
// says who is behind it.

// Keep in step with PostMessage::REACTIONS, the server's whitelist. Keys are
// stored, so this list is append-only.
export const REACTIONS = [
  { key: 'Like', icon: '👍' },
  { key: 'Heart', icon: '❤️' },
  { key: 'Laugh', icon: '😂' },
  { key: 'Celebrate', icon: '🎉' },
  { key: 'Applause', icon: '🙌' },
  { key: 'Fire', icon: '🔥' },
  { key: 'Hundred', icon: '💯' },
  { key: 'MindBlown', icon: '🤯' },
  { key: 'Wow', icon: '😮' },
  { key: 'Sad', icon: '😢' },
  { key: 'Thinking', icon: '🤔' },
  { key: 'Pray', icon: '🙏' },
  { key: 'Strong', icon: '💪' },
  { key: 'Rocket', icon: '🚀' },
  { key: 'Seen', icon: '👀' }
]
const REACTION_ICON = Object.fromEntries(REACTIONS.map(r => [r.key, r.icon]))

// One-click buttons on the hover bar. Three, because the bar also carries
// reply and delete, and a row of six emoji next to those reads as a toolbar.
export const QUICK_REACTIONS = REACTIONS.slice(0, 3)

// Names shown in a hover card before it falls back to "and N others".
const TOOLTIP_NAMES = 3
// Press-and-hold on a pill opens the full list. Touch has no hover.
const LONG_PRESS_MS = 450
// Hover is a sweep, not an intent. Waiting this long before fetching keeps a
// pointer crossing a row of pills from firing a request per pill.
const HOVER_INTENT_MS = 180

export const BarButton = ({ label, onClick, tone = 'default', children }) => (
  <button
    type='button'
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`h-7 min-w-[1.75rem] px-1 flex items-center justify-center rounded text-sm ${
      tone === 'danger'
        ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30'
        : 'text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-white'
    }`}
  >
    {children}
  </button>
)

export const ReactionPicker = ({ onPick, open, setOpen, variant = 'bar' }) => (
  <div className='relative inline-block'>
    {variant === 'plus'
      ? <button
          type='button'
          onClick={() => setOpen(!open)}
          className='inline-flex items-center justify-center h-6 w-8 rounded-full border border-dashed border-gray-300 dark:border-gray-500 text-gray-400 hover:text-cccblue hover:border-cccblue text-sm'
          aria-label='Add reaction'
        >+</button>
      : <BarButton label='Add reaction' onClick={() => setOpen(!open)}>
          <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4' aria-hidden='true'>
            <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7.536 3.05a.75.75 0 011.036.222 3 3 0 004.999 0 .75.75 0 111.248.83 4.5 4.5 0 01-7.496 0 .75.75 0 01.213-1.051z' clipRule='evenodd' />
          </svg>
        </BarButton>}
    {open && <>
      <div className='fixed inset-0 z-10' onClick={() => setOpen(false)} />
      {/* Fixed 2rem tracks, not grid-cols-5: inside a shrink-to-fit absolute box
          the fr columns resolve to zero and the emoji pile on top of each other. */}
      <div className={`absolute grid grid-cols-[repeat(5,2rem)] gap-0.5 p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-600 z-30 ${
        variant === 'plus' ? 'bottom-full mb-1 left-0' : 'top-full mt-1 right-0'
      }`}>
        {REACTIONS.map(r => (
          <button
            key={r.key}
            type='button'
            onClick={() => { onPick(r.key); setOpen(false) }}
            className='h-8 w-8 flex items-center justify-center rounded-md text-xl hover:bg-gray-100 dark:hover:bg-gray-700'
            title={r.key}
          >
            {r.icon}
          </button>
        ))}
      </div>
    </>}
  </div>
)

const reactorName = (user) => user.displayName || user.username || 'Someone'

// "Alice, Bob and Carol" / "Alice, Bob, Carol and 54 others", the tail
// clickable so the full list is one click from the hover card.
const ReactorNames = ({ users, count, onSeeAll }) => {
  const shown = users.slice(0, TOOLTIP_NAMES).map(reactorName)
  const others = Math.max(count - shown.length, 0)
  if (shown.length === 0) return <span>{count} {count === 1 ? 'person' : 'people'}</span>

  const tail = others > 0
    ? <button type='button' onClick={onSeeAll} className='text-cccblue hover:underline font-medium'>
        {others} {others === 1 ? 'other' : 'others'}
      </button>
    : null

  return <span>
    {shown.map((name, i) => (
      <span key={`rn-${i}`}>
        {i > 0 && (i === shown.length - 1 && !tail ? ' and ' : ', ')}
        {name}
      </span>
    ))}
    {tail && <> and {tail}</>}
  </span>
}

const ReactionPill = ({ messageId, reaction, onToggle, fetchReactors, onSeeAll }) => {
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const hoverTimer = useRef(null)
  const pressTimer = useRef(null)
  const longPressed = useRef(false)
  const alive = useRef(true)

  useEffect(() => () => {
    alive.current = false
    clearTimeout(hoverTimer.current)
    clearTimeout(pressTimer.current)
  }, [])

  const load = () => {
    fetchReactors(messageId).then(groups => {
      if (!alive.current || !groups) return
      setDetail(groups.find(g => g.key === reaction.key)?.users || [])
    })
  }

  const onEnter = () => {
    hoverTimer.current = setTimeout(() => { setOpen(true); load() }, HOVER_INTENT_MS)
  }
  const onLeave = () => { clearTimeout(hoverTimer.current); setOpen(false) }

  const onPressStart = () => {
    longPressed.current = false
    pressTimer.current = setTimeout(() => {
      longPressed.current = true
      onSeeAll(messageId, reaction.key)
    }, LONG_PRESS_MS)
  }
  const onPressEnd = () => clearTimeout(pressTimer.current)

  const onClick = () => {
    // The hold already opened the list; don't also toggle.
    if (longPressed.current) { longPressed.current = false; return }
    onToggle(messageId, reaction.key)
  }

  return <span className='relative inline-block' onMouseEnter={onEnter} onMouseLeave={onLeave}>
    <button
      type='button'
      onClick={onClick}
      onTouchStart={onPressStart}
      onTouchEnd={onPressEnd}
      onTouchMove={onPressEnd}
      onContextMenu={(e) => e.preventDefault()}
      aria-pressed={reaction.mine}
      className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-xs font-semibold border select-none ${
        reaction.mine
          ? 'bg-cccblue/10 border-cccblue text-cccblue dark:text-blue-200'
          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
      }`}
    >
      <span className='text-sm leading-none'>{REACTION_ICON[reaction.key] || '❓'}</span>
      {reaction.count}
    </button>
    {open && (
      <span className='absolute bottom-full left-0 mb-1 z-30 w-56 rounded-lg bg-gray-900 text-white text-xs px-3 py-2 shadow-lg'>
        <span className='text-base mr-1'>{REACTION_ICON[reaction.key]}</span>
        {detail
          ? <ReactorNames users={detail} count={reaction.count} onSeeAll={() => onSeeAll(messageId, reaction.key)} />
          : 'Counting heads…'}
      </span>
    )}
  </span>
}

export const ReactionsRow = ({ message, onToggle, fetchReactors, onSeeAll }) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const reactions = message.reactions || []
  if (reactions.length === 0) return null

  return <div className='mt-1 flex flex-wrap items-center gap-1'>
    {reactions.map(r => (
      <ReactionPill
        key={r.key}
        messageId={message.id}
        reaction={r}
        onToggle={onToggle}
        fetchReactors={fetchReactors}
        onSeeAll={onSeeAll}
      />
    ))}
    <ReactionPicker variant='plus' open={pickerOpen} setOpen={setPickerOpen} onPick={(key) => onToggle(message.id, key)} />
  </div>
}

// Everyone behind every reaction on one message, tabbed by emoji.
export const ReactionsModal = ({ target, onClose, fetchReactors }) => {
  const [groups, setGroups] = useState(null)
  const [activeKey, setActiveKey] = useState(target?.key)

  useEffect(() => {
    if (!target) { setGroups(null); return }
    setActiveKey(target.key)
    let live = true
    fetchReactors(target.messageId).then(g => { if (live) setGroups(g || []) })
    return () => { live = false }
  }, [target])

  useEffect(() => {
    if (!target) return undefined
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [target])

  if (!target) return null
  const active = groups?.find(g => g.key === activeKey) || groups?.[0]

  return <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center' role='dialog' aria-modal='true'>
    <div className='absolute inset-0 bg-black/40' onClick={onClose} />
    <div className='relative w-full sm:max-w-md max-h-[70vh] flex flex-col rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-900 shadow-xl'>
      <div className='flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700'>
        <h2 className='text-sm font-bold text-gray-900 dark:text-gray-100'>Reactions</h2>
        <button type='button' onClick={onClose} className='text-gray-400 hover:text-gray-700 dark:hover:text-white' aria-label='Close'>✕</button>
      </div>
      {!groups
        ? <div className='p-6 text-sm text-center text-gray-500'>Rounding up the fans…</div>
        : <div className='flex flex-col sm:flex-row min-h-0'>
            <div className='shrink-0 flex sm:flex-col gap-1 p-2 overflow-x-auto sm:overflow-y-auto border-b sm:border-b-0 sm:border-r border-gray-100 dark:border-gray-700 sm:w-24'>
              {groups.map(g => (
                <button
                  key={g.key}
                  type='button'
                  onClick={() => setActiveKey(g.key)}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-sm ${active?.key === g.key ? 'bg-gray-100 dark:bg-gray-700 font-semibold' : 'hover:bg-gray-50 dark:hover:bg-gray-800'} text-gray-700 dark:text-gray-200`}
                >
                  <span>{REACTION_ICON[g.key]}</span>{g.users.length}
                </button>
              ))}
            </div>
            <ul className='flex-1 min-h-0 overflow-y-auto p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]'>
              {(active?.users || []).map(u => (
                <li key={u.id} className='px-2 py-1.5 text-sm text-gray-700 dark:text-gray-200'>
                  {reactorName(u)} {u.username && <span className='text-xs text-gray-400'>@{u.username}</span>}
                </li>
              ))}
              {groups.length === 0 && <li className='px-2 py-1.5 text-sm text-gray-500'>Everyone took their reactions back. Fickle bunch.</li>}
            </ul>
          </div>}
    </div>
  </div>
}

BarButton.propTypes = { label: PropTypes.string, onClick: PropTypes.func, tone: PropTypes.string, children: PropTypes.node }
ReactionPicker.propTypes = { onPick: PropTypes.func, open: PropTypes.bool, setOpen: PropTypes.func, variant: PropTypes.string }
ReactorNames.propTypes = { users: PropTypes.array, count: PropTypes.number, onSeeAll: PropTypes.func }
ReactionPill.propTypes = { messageId: PropTypes.number, reaction: PropTypes.object, onToggle: PropTypes.func, fetchReactors: PropTypes.func, onSeeAll: PropTypes.func }
ReactionsRow.propTypes = { message: PropTypes.object, onToggle: PropTypes.func, fetchReactors: PropTypes.func, onSeeAll: PropTypes.func }
ReactionsModal.propTypes = { target: PropTypes.object, onClose: PropTypes.func, fetchReactors: PropTypes.func }
